import React from 'react';
import UICircleChart, { UICircleChartSegment } from '@/components/shared/UICircleChart';
import './UIStatCardProperty.scss';

export type PropertyIconType = 'buildings' | 'dollar' | 'factory' | 'home';

export interface PropertyStatItem {
  label: string;
  value: string;
  color: string;
  percentage: number;
}

export interface UIStatCardPropertyProps {
  /**
   * Назва типу власності
   */
  title: string;

  /**
   * Тип іконки
   */
  iconType: PropertyIconType;

  /**
   * Колір фону іконки
   */
  iconBackgroundColor?: string;

  /**
   * Загальна кількість одиниць
   */
  totalUnits: number;

  /**
   * Статистика по категоріях
   */
  stats: PropertyStatItem[];

  /**
   * Обробник кліку на меню
   */
  onMenuClick?: () => void;

  /**
   * Обробник кліку на карточку
   */
  onClick?: () => void;

  /**
   * Додатковий CSS клас
   */
  className?: string;
}

const baseClass = 'UIStatCardProperty';

// Іконки
const IconBuildings = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M10 20V14H14V20H19V12H22L12 3L2 12H5V20H10Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const IconDollar = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 1V23M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6312 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6312 13.6815 18 14.5717 18 15.5C18 16.4283 17.6312 17.3185 16.9749 17.9749C16.3185 18.6312 15.4283 19 14.5 19H6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconFactory = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M9 20V10L3 15V20H9ZM9 20H21V16L15 11V20M9 20V14M9 10V4L15 9M7 3L13 8M17 3L23 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const IconHome = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M9 22V12H15V22"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const icons = {
  buildings: IconBuildings,
  dollar: IconDollar,
  factory: IconFactory,
  home: IconHome,
};

// Мокові дані
const mockData: Partial<UIStatCardPropertyProps> = {
  title: 'Residential buildings',
  iconType: 'home',
  iconBackgroundColor: '#5CB2EF',
  totalUnits: 289,
  stats: [
    { label: 'Paid', value: '64%', color: '#76C102', percentage: 64 },
    { label: 'Arrears', value: '22%', color: '#FB2F54', percentage: 22 },
    { label: 'Not set', value: '12%', color: '#FF9C07', percentage: 12 },
  ],
};

const UIStatCardProperty: React.FC<UIStatCardPropertyProps> = ({
                                                                 title = mockData.title!,
                                                                 iconType = mockData.iconType!,
                                                                 iconBackgroundColor = mockData.iconBackgroundColor!,
                                                                 totalUnits = mockData.totalUnits!,
                                                                 stats = mockData.stats!,
                                                                 onMenuClick = () => console.log('Menu clicked'),
                                                                 onClick,
                                                                 className = '',
                                                               }) => {
  const IconComponent = icons[iconType];

  // Підготовка даних для діаграми
  const chartSegments: UICircleChartSegment[] = stats.map((stat) => ({
    value: stat.percentage,
    percentage: stat.percentage,
    color: stat.color,
    label: stat.label,
  }));

  const containerClasses = [
    baseClass,
    onClick && `${baseClass}--clickable`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClasses} onClick={onClick}>
      {/* Заголовок */}
      <div className={`${baseClass}__title`}>
        <div className={`${baseClass}__text-line`}>
          <div className={`${baseClass}__text`}>
            <div
              className={`${baseClass}__icon`}
              style={{ backgroundColor: iconBackgroundColor }}
            >
              <IconComponent />
            </div>
            <h3 className={`${baseClass}__heading`}>{title}</h3>
          </div>
          <button
            className={`${baseClass}__menu-btn`}
            onClick={(e) => {
              e.stopPropagation();
              onMenuClick();
            }}
            aria-label="More options"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className={`${baseClass}__separator`}></div>
      </div>

      {/* Контент */}
      <div className={`${baseClass}__data`}>
        <div className={`${baseClass}__stats`}>
          {/* Total units */}
          <div className={`${baseClass}__substat ${baseClass}__substat--total`}>
            <div className={`${baseClass}__category`}>
              <span className={`${baseClass}__label`}>Total units</span>
            </div>
            <span className={`${baseClass}__value ${baseClass}__value--large`}>
              {totalUnits}
            </span>
          </div>

          {/* Статистика по категоріях */}
          {stats.map((stat, index) => (
            <div key={index} className={`${baseClass}__substat`}>
              <div className={`${baseClass}__category`}>
                <span
                  className={`${baseClass}__dot`}
                  style={{ backgroundColor: stat.color }}
                ></span>
                <span className={`${baseClass}__label`}>{stat.label}</span>
              </div>
              <span className={`${baseClass}__value`}>{stat.value}</span>
            </div>
          ))}
        </div>

        {/* Діаграма */}
        <div className={`${baseClass}__graph`}>
          <UICircleChart
            segments={chartSegments}
            size={88}
            strokeWidth={16}
            backgroundColor="#F4F6F8"
          />
        </div>
      </div>
    </div>
  );
};

export default UIStatCardProperty;